import Layout from "../Layout/Layout";
import React, { useState, useEffect } from 'react';
import Axios from 'axios';
import Constant from '../views/Constant';
import { Helmet } from "react-helmet-async";
import styled from "styled-components";
import { ChevronDown, HelpCircle } from "lucide-react";

const DEFAULT_FAQS = [
  {
    _id: "default-1",
    question: "What is StoryTent?",
    answer: "StoryTent is an interactive creative platform for writers, poets, and storytellers to publish original works, participate in open mics, join contests, and connect with fellow creators worldwide."
  },
  {
    _id: "default-2",
    question: "How do I publish a story or poem on StoryTent?",
    answer: "Create an account or log in, navigate to the 'Create' section in the top navigation bar, choose whether you want to write a story or poem, fill in your title, content, and category, and submit your work for publishing."
  },
  {
    _id: "default-3",
    question: "Can I participate in online Open Mics and Contests?",
    answer: "Yes! Visit the 'Events' or 'Contests' section to view upcoming online open mics, themed contests, and live meetings. You can register directly from each event page."
  },
  {
    _id: "default-4",
    question: "Is StoryTent free to use?",
    answer: "StoryTent offers free access to read, write, and explore creative content, alongside premium event participation and special contest categories."
  },
  {
    _id: "default-5",
    question: "How do I manage or update my creator profile?",
    answer: "Click on your Profile in the top navigation bar to edit your bio, update your profile picture, view follower counts, and browse your published stories and poems."
  }
];

const isDummyText = (text) => {
  if (!text || typeof text !== 'string') return true;
  const dummyPatterns = /^(xcvzxc|xcxzc|sasas|asd|csdcsd|test|dummy|asdf|qwerty|1234)/i;
  return dummyPatterns.test(text.trim()) || text.trim().length < 3;
};

export default function FAQ() {
  const [faqs, setFaqs] = useState(DEFAULT_FAQS);
  const [openIndex, setOpenIndex] = useState(0); // first item open by default

  useEffect(() => {
    try {
      Axios.post(Constant.apiBasePath + 'question-answer').then(response => {
        const { data } = response;
        if (data.status === Constant.statusSuccess && Array.isArray(data.data) && data.data.length > 0) {
          const cleanFaqs = data.data.filter(item => 
            item && item.question && item.answer &&
            !isDummyText(item.question) && !isDummyText(item.answer)
          );
          if (cleanFaqs.length > 0) {
            setFaqs(cleanFaqs);
          }
        }
      }).catch((error) => {
        console.error("Error fetching FAQs:", error);
      });
    } catch (error) {
      console.error(error);
    }
  }, []);

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? -1 : index);
  };

  return (
    <Layout>
      <Helmet>
        <title>Frequently Asked Questions (FAQs) | StoryTent</title>
        <meta name="description" content="Find answers to frequently asked questions about StoryTent, publishing stories and poems, joining open mics, and managing your creator account." />
        <meta property="og:title" content="Frequently Asked Questions (FAQs) | StoryTent" />
        <meta property="og:description" content="Find answers to frequently asked questions about StoryTent." />
        <meta property="og:url" content="https://www.storytent.in/faqs" />
        <link rel="canonical" href="https://www.storytent.in/faqs" />
      </Helmet>

      <PageWrapper>
        <div className="container">
          <HeaderSection>
            <IconBadge>
              <HelpCircle size={28} color="#2dd4bf" />
            </IconBadge>
            <MainTitle>Frequently Asked Questions</MainTitle>
            <SubTitle>Everything you need to know about StoryTent, publishing, events, and community features.</SubTitle>
          </HeaderSection>

          <FaqContainer>
            {faqs.map((faq, index) => {
              const isOpen = openIndex === index;
              return (
                <FaqCard key={faq._id || index} $isOpen={isOpen}>
                  <FaqQuestionButton 
                    type="button" 
                    onClick={() => toggleFAQ(index)}
                    aria-expanded={isOpen}
                  >
                    <span>{faq.question}</span>
                    <ChevronWrapper $isOpen={isOpen}>
                      <ChevronDown size={20} />
                    </ChevronWrapper>
                  </FaqQuestionButton>
                  <FaqAnswerWrapper $isOpen={isOpen}>
                    <FaqAnswerText>{faq.answer}</FaqAnswerText>
                  </FaqAnswerWrapper>
                </FaqCard>
              );
            })}
          </FaqContainer>
        </div>
      </PageWrapper>
    </Layout>
  );
}

const PageWrapper = styled.div`
  background: transparent;
  color: var(--color-text, #ffffff);
  padding: 30px 0 60px;
  min-height: 80vh;
`;

const HeaderSection = styled.div`
  text-align: center;
  margin-bottom: 36px;
`;

const IconBadge = styled.div`
  width: 54px;
  height: 54px;
  border-radius: 50%;
  background: rgba(45, 212, 191, 0.12);
  border: 1px solid rgba(45, 212, 191, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
`;

const MainTitle = styled.h1`
  color: var(--color-text, #ffffff);
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 10px;

  @media (max-width: 768px) {
    font-size: 1.6rem;
  }
`;

const SubTitle = styled.p`
  color: var(--color-text-secondary, #94a3b8);
  font-size: 1.05rem;
  max-width: 620px;
  margin: 0 auto;
  line-height: 1.5;
`;

const FaqContainer = styled.div`
  max-width: 820px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const FaqCard = styled.div`
  background: var(--color-surface, #141f1e);
  border: 1px solid ${({ $isOpen }) => ($isOpen ? "rgba(45, 212, 191, 0.4)" : "var(--color-border, rgba(255, 255, 255, 0.1))")};
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  transition: all 0.25s ease-in-out;

  &:hover {
    border-color: rgba(45, 212, 191, 0.35);
  }
`;

const FaqQuestionButton = styled.button`
  width: 100%;
  padding: 18px 22px;
  background: transparent;
  border: none;
  display: flex;
  justify-content: space-between;
  align-items: center;
  text-align: left;
  font-size: 1.08rem;
  font-weight: 600;
  color: var(--color-text, #ffffff);
  cursor: pointer;
  outline: none;
  gap: 12px;

  &:hover {
    color: #2dd4bf;
  }
`;

const ChevronWrapper = styled.div`
  color: ${({ $isOpen }) => ($isOpen ? "#2dd4bf" : "var(--color-text-secondary, #94a3b8)")};
  transform: ${({ $isOpen }) => ($isOpen ? "rotate(180deg)" : "rotate(0deg)")};
  transition: transform 0.25s ease;
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

const FaqAnswerWrapper = styled.div`
  max-height: ${({ $isOpen }) => ($isOpen ? "400px" : "0")};
  opacity: ${({ $isOpen }) => ($isOpen ? "1" : "0")};
  overflow: hidden;
  transition: max-height 0.3s ease, opacity 0.25s ease;
`;

const FaqAnswerText = styled.div`
  padding: 0 22px 20px;
  font-size: 0.98rem;
  line-height: 1.7;
  color: var(--color-text-secondary, #cbd5e1);
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  padding-top: 14px;
`;